/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { DataApiService } from './data-api-service';
import { EntityInfoService } from '../metadata-api/entity-info.service';

export interface FilterListEntry {
  filter: string;
  values: any[];
}

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private filtersList$?: Observable<FilterListEntry[]>;

  /** `lang` values from the last successful filter_list fetch; undefined until loaded. */
  availableLanguages?: string[];

  constructor(
    private dataApi: DataApiService,
    private entityInfoService: EntityInfoService,
  ) {}

  getFiltersList(): Observable<FilterListEntry[]> {
    if (!this.filtersList$) {
      this.filtersList$ = this.dataApi
        .get<FilterListEntry[]>('/v2/filter_list', null)
        .pipe(
          mergeMap((res) =>
            forkJoin({
              continents: this.entityInfoService.getContinents(),
              countries: this.entityInfoService.getCountries(),
              events: this.entityInfoService.getHierarchyInfo('event', {}),
            }).pipe(map((enrichment) => this.assemble(res, enrichment))),
          ),
          tap((list) => {
            const langValues = list.find((el) => el.filter === 'lang')?.values;
            if (langValues?.length) {
              this.availableLanguages = langValues.map((v: any) => v.value);
            }
          }),
          catchError((err: HttpErrorResponse) => {
            if (err.error instanceof Error) {
              console.error('An error occurred:', err.error.message);
            } else {
              console.error(
                `Backend returned code ${err.status}, body was: ${err.error}`,
              );
            }
            this.filtersList$ = undefined; // allow next caller to retry
            return of<FilterListEntry[]>([]);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    }
    return this.filtersList$;
  }

  private assemble(
    base: FilterListEntry[],
    enrichment: { continents: any; countries: any; events: any },
  ): FilterListEntry[] {
    const filtersArray: FilterListEntry[] = base.filter(
      (el) =>
        !['source_continents', 'source_countries', 'events'].includes(
          el.filter,
        ),
    );
    filtersArray.push({
      filter: 'source_continents',
      values: enrichment.continents.entities.map((el: any) => ({
        value: el.alpha_id,
        name: el.name,
      })),
    });
    filtersArray.push({
      filter: 'source_countries',
      values: enrichment.countries.entities.map((el: any) => ({
        value: el.alpha_id,
        name: el.name,
      })),
    });
    filtersArray.push({
      filter: 'events',
      values: enrichment.events.event_structure,
    });
    return filtersArray;
  }
}
